import { CategoryTemplatePlaceholder } from '@/javascripts/category_template_placeholder';
import { PostBodyDirtyTracker } from '@/javascripts/post_body_dirty_tracker';
import { confirmCategoryTemplate } from '@/utils/category_template_confirm';
import {
  convertCkHtml,
  convertHtmlToPlainText,
  convertToEditorHtml,
} from './suneditor/convert_ck_contents';

const PREFIX = 'category_';

class CategoryTemplate {
  // container を指定すると、カテゴリ select と本文欄の探索をその要素配下に限定する
  // (誘導モーダルなど、ページ本体側のフォームを巻き込みたくない場合に使う)。
  constructor(container = document) {
    this.container = container;
    this.removeAllIem();

    const self = this;
    // research 経由の投稿 (research_answer_id 付き URL) では回答内容が本文にプリセットされ、
    // カテゴリを手動で切り替える運用があるため、切替時に本文を消さない
    // COMMUNITY-4612 以前の挙動を維持する (COMMUNITY-4680)
    const isResearchPost = new URLSearchParams(window.location.search).has(
      'research_answer_id',
    );
    const $categorySelect = $(container).find('.js-category-id');
    let categoryVal = PREFIX + $categorySelect.val();

    // research 経由は 4680 の挙動を維持するため、ダーティ判定・確認モーダルの対象外とする
    this.dirtyTracker = isResearchPost
      ? null
      : new PostBodyDirtyTracker(container);
    // キャンセルで元のカテゴリへ戻すときに、自分の change ハンドラを素通りさせるためのフラグ
    this.isRevertingCategory = false;
    // 確認モーダルの表示中フラグ。要求の二重化を防ぐ
    this.isConfirming = false;

    // 「プレースホルダーにする」設定のテンプレートは本文ではなくプレースホルダーとして表示する。
    // 初期表示のカテゴリがプレースホルダー設定の場合、サーバはテンプレートを本文に入れず
    // form の data-category-template-placeholder で渡してくる
    this.placeholder = new CategoryTemplatePlaceholder(container);
    const initialPlaceholder = $categorySelect.get(0)?.closest('form')?.dataset
      .categoryTemplatePlaceholder;
    if (initialPlaceholder) this.placeholder.show(initialPlaceholder);

    $categorySelect.on('change', function () {
      const type = $(this).data('type');
      const categorySelect = this;
      const requestedCategoryId = categorySelect.value;
      const previousCategoryId = categoryVal.slice(PREFIX.length);

      if (isResearchPost) {
        self.saveBody(type, categoryVal);
        categoryVal = PREFIX + requestedCategoryId;

        const savedBody = localStorage.getItem(categoryVal);
        if (savedBody) return self.setBody(type, savedBody);
        if (!requestedCategoryId) return;

        $.ajax({
          type: 'GET',
          url: `/${type}_categories/${requestedCategoryId}/template`,
          dataType: 'json',
        })
          .done(function (data) {
            if (data.use_as_placeholder) {
              self.placeholder.show(data.body);
              return;
            }
            self.placeholder.clear();
            if (data.body) self.setBody(type, data.body);
          })
          .fail(function (e) {
            console.error(e);
          });
        return;
      }

      categoryVal = PREFIX + requestedCategoryId;

      // キャンセルで元のカテゴリへ戻したときの change。選択状態を同期するだけで本文には触れない
      if (self.isRevertingCategory) return;

      // カテゴリ未選択にはテンプレートが無い。取得せず「テンプレートなし」として判定する
      if (!requestedCategoryId) {
        self.decideByDirtyState(type, categorySelect, '', previousCategoryId);
        return;
      }

      $.ajax({
        type: 'GET',
        url: `/${type}_categories/${requestedCategoryId}/template`,
        dataType: 'json',
      })
        .done(function (data) {
          // 高速に切り替えたとき、古いレスポンスで現在の選択を上書きしない
          if (categorySelect.value !== requestedCategoryId) return;
          self.decideByDirtyState(
            type,
            categorySelect,
            data.body || '',
            previousCategoryId,
            data.use_as_placeholder === true,
          );
        })
        .fail(function (e) {
          // 取得できない場合は本文に触れない (= 保持) 側へ倒す
          console.error(e);
        });
    });
  }

  // 「本文に手を入れたか」×「変更先にテンプレートがあるか」の 2 条件で分岐する (COMMUNITY-4690)。
  // テンプレート本文が空のレコードは API 上「未設定」と区別できないため、どちらも「なし」として扱う。
  async decideByDirtyState(
    type,
    categorySelect,
    templateBody,
    previousCategoryId,
    useAsPlaceholder = false,
  ) {
    const hasTemplate = templateBody.trim() !== '';

    // 変更先のテンプレートがプレースホルダー設定: 本文を上書きしないので確認不要。
    // 未編集なら（前カテゴリのテンプレート本文が残っていれば）本文を空にしてプレースホルダーを見せ、
    // 編集済みなら入力を保持したままプレースホルダーだけ差し替える
    if (useAsPlaceholder) {
      if (!this.dirtyTracker.isDirty) this.applyTemplate(type, '');
      this.placeholder.show(templateBody);
      return;
    }

    // 未編集: 失うものが無いので従来どおり黙って反映する (パターン #1 #2 #5 #6)
    if (!this.dirtyTracker.isDirty) {
      this.placeholder.clear();
      this.applyTemplate(type, templateBody);
      return;
    }

    // 編集済み × 変更先にテンプレート無し: 上書きするものが無いので入力を保持する (#3 #7)
    if (!hasTemplate) {
      this.placeholder.clear();
      return;
    }

    // 編集済み × 変更先にテンプレート有り: 不可逆な損失になるため確認する (#4 #8)
    //
    // 表示中は overlay で select を触れないが、テンプレート取得の応答が返る前に
    // X → Y → X と素早く切り替えると X の要求が 2 本とも staleness ガードを通過し、
    // 確認要求が二重に飛ぶ。後から来た要求で先の要求が cancel 扱いになると
    // select だけ巻き戻り、本文との不整合が起きるため、確認中の要求は捨てる。
    if (this.isConfirming) return;
    this.isConfirming = true;
    let choice;
    try {
      choice = await confirmCategoryTemplate();
    } finally {
      this.isConfirming = false;
    }

    if (choice === 'apply') {
      this.placeholder.clear();
      this.applyTemplate(type, templateBody);
      return;
    }
    if (choice === 'cancel') {
      // 元のカテゴリへ戻すので、元カテゴリのプレースホルダー表示はそのまま残す
      this.revertCategory(categorySelect, previousCategoryId);
      return;
    }
    // keep: 本文もダーティフラグもそのまま (連続変更で都度確認するため reset しない)
    this.placeholder.clear();
  }

  // テンプレート適用はプログラムからの書き換えなので、ダーティ判定に混ぜない
  applyTemplate(type, body) {
    this.dirtyTracker.suppress(() => this.setBody(type, body));
    this.dirtyTracker.reset();
  }

  // カテゴリ変更自体を取り消す。カテゴリ連動のプリセットハッシュタグも戻したいので
  // native な change を投げ直す (jQuery の .trigger('change') は addEventListener 登録のリスナに届かない)
  revertCategory(categorySelect, previousCategoryId) {
    this.isRevertingCategory = true;
    try {
      categorySelect.value = previousCategoryId;
      categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      this.isRevertingCategory = false;
    }
  }

  isAnnouncement(type) {
    return type === 'announcement';
  }

  // announcement は text/markdown/link をラジオボタンで切り替えられる (お知らせのみ)。
  // markdown 選択時は本文の実体が f.text_area :markdown 側になるため、
  // getBody/setBody もこちらを対象にする必要がある。
  isAnnouncementMarkdown() {
    return (
      $('input[name="announcement[announcement_type]"][type=hidden]').val() ===
      'markdown'
    );
  }

  // ページレイアウト未適用のトップ (communities/show) にはタイトル表示用の div#editor があり、
  // 誘導モーダルの本文欄と id が衝突するため container 配下から探す。
  // 本文欄は常に textarea なので、要素型でも絞って div#editor を拾わないようにする
  findAnnouncementEditor() {
    return $(this.container).find('textarea#editor');
  }

  isChat(type) {
    return type === 'chat';
  }

  saveBody(type, key) {
    localStorage.setItem(key, this.getBody(type));
  }

  getBody(type) {
    // #chat_text_body / #editor は SunEditor の onChange で常に正規化後の内容に同期されるため、
    // 生の window.suneditor.$.html.get() ではなく textarea の値を参照する
    // (エディタ未初期化時にも同じ値を返せる)
    if (this.isChat(type)) {
      const bodyFormat = $('#chat_body_format').val();
      if (bodyFormat === 'markdown') {
        return $('#chat_markdown_body').val();
      }
      return $('#chat_text_body').val();
    }

    if (this.isAnnouncement(type) && this.isAnnouncementMarkdown()) {
      return $('#announcement_markdown').val();
    }

    if (this.isAnnouncement(type)) {
      return this.findAnnouncementEditor().val();
    }

    return $(this.container).find('.js-category-template-body').val();
  }

  setBody(type, body) {
    if (this.isChat(type)) {
      const bodyFormat = $('#chat_body_format').val();
      $('#chat_markdown_body').val(
        bodyFormat === 'markdown' ? body : convertHtmlToPlainText(body),
      );

      if (window.suneditor) {
        window.suneditor.$.html.set(convertToEditorHtml(body));
      } else {
        $('#chat_text_body').val(convertHtmlToPlainText(body));
      }
      return;
    }

    // announcement の markdown 選択時は、非表示の SunEditor 側 (#editor) ではなく
    // 実際に表示されている f.text_area :markdown を書き換える (COMMUNITY-4690)。
    // テンプレート本文は常に HTML (管理画面の SunEditor で入力) のため、
    // プレーンテキストへ変換してから書き込む。
    if (this.isAnnouncement(type) && this.isAnnouncementMarkdown()) {
      $('#announcement_markdown').val(convertHtmlToPlainText(body));
      return;
    }

    if (this.isAnnouncement(type)) {
      const converted = convertCkHtml(body);
      if (window.suneditor) {
        window.suneditor.$.html.set(converted);
      } else {
        this.findAnnouncementEditor().val(converted);
      }
      return;
    }

    $(this.container).find('.js-category-template-body').val(body);
  }

  removeAllIem() {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        if (key && key.indexOf(PREFIX) === 0) localStorage.removeItem(key);
      }
    }
  }
}

export { CategoryTemplate };
